import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Heart, X, MessageCircle, MapPin, Sparkles, Trophy, Eye, Globe, Video, Phone, CheckCircle, Ban, Gift, Wallet, Loader2, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import ProfileVideoPlayer from '../components/ProfileVideoPlayer';
import BlockButton from '../components/BlockButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [matchId, setMatchId] = useState(null);
  const [isMatch, setIsMatch] = useState(false);
  const [voteCount, setVoteCount] = useState(0);
  const [hasVoted, setHasVoted] = useState(false);
  const [voting, setVoting] = useState(false);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [showGiveVotesModal, setShowGiveVotesModal] = useState(false);
  const [voteBalance, setVoteBalance] = useState(0);
  const [votesToGive, setVotesToGive] = useState(1);
  const [givingVotes, setGivingVotes] = useState(false);
  const [dailyVoteStatus, setDailyVoteStatus] = useState(null);

  useEffect(() => {
    loadProfile();
    checkIfMatch();
    checkIfOwnProfile();
    loadVoteBalance();
    loadDailyVoteStatus();
  }, [userId]);

  useEffect(() => {
    if (userId) {
      loadVotes();
      checkUserVoted();
    }
  }, [userId]);

  const loadDailyVoteStatus = async () => {
    try {
      const response = await axios.get(`${API}/daily-vote-status`, { withCredentials: true });
      setDailyVoteStatus(response.data);
    } catch (error) {
      console.error('Failed to load daily vote status:', error);
    }
  };

  const loadVoteBalance = async () => {
    try {
      const response = await axios.get(`${API}/vote-balance`, { withCredentials: true });
      setVoteBalance(response.data.vote_balance || 0);
    } catch (error) {
      console.error('Failed to load vote balance:', error);
    }
  };

  const handleGiveVotes = async () => {
    if (votesToGive <= 0 || votesToGive > voteBalance) {
      toast.error('Invalid vote count');
      return;
    }
    
    setGivingVotes(true);
    try {
      const response = await axios.post(
        `${API}/give-votes`,
        { recipient_id: userId, vote_count: votesToGive },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setVoteCount(response.data.recipient_total_votes);
      setVoteBalance(response.data.your_remaining_balance);
      setShowGiveVotesModal(false);
      setVotesToGive(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to give votes');
    } finally {
      setGivingVotes(false);
    }
  };

  const loadProfile = async () => {
    try {
      // First check if it's the current user's own profile
      const meResponse = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      
      if (meResponse.data.id === userId) {
        // It's their own profile, use the me endpoint data
        console.log('Profile data:', meResponse.data);
        console.log('Relationship status:', meResponse.data.relationship_status);
        setProfile(meResponse.data);
        setIsOwnProfile(true);
        setCurrentUserId(meResponse.data.id);
        setLoading(false);
        return;
      }
      
      setCurrentUserId(meResponse.data.id);
      
      // Try to get user directly by ID (works for any user)
      try {
        const userResponse = await axios.get(`${API}/users/${userId}`, {
          withCredentials: true
        });
        setProfile(userResponse.data);
        setLoading(false);
        return;
      } catch (e) {
        // If direct lookup fails, fall back to old method
        console.log('Direct user lookup failed, trying discovery/matches...');
      }
      
      // Fallback: Get all users from discover endpoint
      const response = await axios.get(`${API}/discover`, {
        withCredentials: true
      });
      
      // Find the specific user
      const user = response.data.find(u => u.id === userId);
      
      if (user) {
        setProfile(user);
      } else {
        // If not in discover, check matches
        const matchesResponse = await axios.get(`${API}/matches`, {
          withCredentials: true
        });
        
        const match = matchesResponse.data.find(m => m.user.id === userId);
        if (match) {
          setProfile({...match.user, isFromMatch: true});
          setMatchId(match.match_id);
          setIsMatch(true);
        } else {
          toast.error('User not found');
          navigate('/discover');
        }
      }
      setLoading(false);
    } catch (e) {
      console.error('Failed to load profile:', e);
      toast.error('Failed to load profile');
      navigate('/discover');
    }
  };

  const checkIfMatch = async () => {
    try {
      const response = await axios.get(`${API}/matches`, {
        withCredentials: true
      });
      
      const match = response.data.find(m => m.user.id === userId);
      if (match) {
        setMatchId(match.match_id);
        setIsMatch(true);
      }
    } catch (e) {
      console.error('Failed to check match:', e);
    }
  };

  const checkIfOwnProfile = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true
      });
      setCurrentUserId(response.data.id);
      setIsOwnProfile(response.data.id === userId);
    } catch (e) {
      console.error('Failed to check user:', e);
    }
  };

  const handleSwipe = async (action) => {
    try {
      const response = await axios.post(`${API}/swipe`, {
        target_user_id: userId,
        action: action
      }, { withCredentials: true });

      if (response.data.matched) {
        toast.success(`It's a match! 💕`);
        await checkIfMatch();
      } else {
        toast.success(action === 'like' ? 'Profile liked!' : 'Passed');
        navigate('/discover');
      }
    } catch (e) {
      toast.error('Failed to process');
    }
  };

  const handleMessage = () => {
    if (matchId) {
      navigate(`/chat/${matchId}`);
    } else {
      toast.error('You need to match first!');
    }
  };

  const loadVotes = async () => {
    try {
      const response = await axios.get(`${API}/votes/${userId}`, {
        withCredentials: true
      });
      setVoteCount(response.data.vote_count);
    } catch (e) {
      console.error('Failed to load votes:', e);
    }
  };

  const checkUserVoted = async () => {
    try {
      const response = await axios.get(`${API}/votes/${userId}/check`, {
        withCredentials: true
      });
      setHasVoted(response.data.has_voted);
    } catch (e) {
      console.error('Failed to check vote status:', e);
    }
  };

  const handleVote = async () => {
    if (voting || hasVoted) return;
    
    setVoting(true);
    try {
      const response = await axios.post(`${API}/vote`, {
        voted_for_id: userId
      }, { withCredentials: true });
      
      setVoteCount(response.data.vote_count);
      setHasVoted(true);
      toast.success('Vote submitted! 🎉');
    } catch (e) {
      if (e.response?.status === 400) {
        toast.info('You already voted for this person!');
        setHasVoted(true);
      } else {
        toast.error('Failed to vote');
      }
    } finally {
      setVoting(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-pink-400 border-t-transparent"></div>
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const photos = profile.photos || [];
  
  // Create media array with video first (if exists), then photos
  const mediaItems = [];
  if (profile.profile_video_url) {
    mediaItems.push({
      type: 'video',
      url: profile.profile_video_url.startsWith('http') 
        ? profile.profile_video_url 
        : `${BACKEND_URL}${profile.profile_video_url}`
    });
  }
  photos.forEach(photo => {
    mediaItems.push({
      type: 'photo',
      url: photo.startsWith('http') ? photo : `${BACKEND_URL}${photo}`
    });
  });
  
  const currentMedia = mediaItems[currentPhotoIndex] || { type: 'photo', url: 'https://via.placeholder.com/400x600?text=No+Photo' };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center space-x-4">
          <Button
            data-testid="back-btn"
            onClick={() => navigate(-1)}
            variant="ghost"
            size="icon"
            className="hover:bg-pink-100 rounded-full"
          >
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </Button>
          <h1 className="text-xl font-bold text-gray-800">{profile.name}'s Profile</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Preview Banner */}
        {isOwnProfile && (
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl p-6 mb-8 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Eye className="w-6 h-6" />
                <div>
                  <h3 className="text-lg font-bold">Preview Mode</h3>
                  <p className="text-sm text-purple-50">This is how other users see your profile</p>
                </div>
              </div>
              <Button
                data-testid="edit-profile-btn"
                onClick={() => navigate('/setup')}
                className="bg-white text-purple-600 hover:bg-purple-50 rounded-full"
              >
                Edit Profile
              </Button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Photos Section */}
          <div className="space-y-4">
            <div className="relative bg-white rounded-3xl shadow-2xl overflow-hidden" style={{height: '600px'}}>
              {currentMedia.type === 'video' ? (
                <ProfileVideoPlayer 
                  videoUrl={currentMedia.url}
                  className="h-full"
                />
              ) : (
                <img
                  src={currentMedia.url}
                  alt={profile.name}
                  className="w-full h-full object-cover"
                />
              )}
              
              {/* Photo Navigation */}
              {mediaItems.length > 1 && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex space-x-2">
                  {mediaItems.map((item, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`w-2 h-2 rounded-full ${
                        idx === currentPhotoIndex ? 'bg-white' : 'bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}
              
              {/* Navigation Arrows */}
              {mediaItems.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex - 1 + mediaItems.length) % mediaItems.length)}
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-800" />
                  </button>
                  <button
                    onClick={() => setCurrentPhotoIndex((currentPhotoIndex + 1) % mediaItems.length)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2"
                  >
                    <ArrowLeft className="w-5 h-5 text-gray-800 transform rotate-180" />
                  </button>
                </>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center items-center space-x-6">
              {!isMatch && !profile.isFromMatch && (
                <>
                  <Button
                    data-testid="pass-btn"
                    onClick={() => handleSwipe('pass')}
                    size="icon"
                    className="w-16 h-16 rounded-full bg-white hover:bg-red-50 shadow-xl border-2 border-red-200 hover:border-red-400"
                  >
                    <X className="w-7 h-7 text-red-500" />
                  </Button>
                  
                  <Button
                    data-testid="like-btn"
                    onClick={() => handleSwipe('like')}
                    size="icon"
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-500 to-red-500 hover:from-pink-600 hover:to-red-600 shadow-2xl transform hover:scale-110"
                  >
                    <Heart className="w-9 h-9 text-white fill-white" />
                  </Button>
                </>
              )}
              
              {isMatch && (
                <Button
                  data-testid="message-btn"
                  onClick={handleMessage}
                  className="w-full max-w-xs bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white rounded-full py-6 text-lg font-semibold"
                >
                  <MessageCircle className="w-5 h-5 mr-2" />
                  Send Message
                </Button>
              )}
              
              {isMatch && (
                <div className="flex space-x-3 max-w-xs w-full mt-3">
                  <Button
                    data-testid="video-call-btn"
                    onClick={() => navigate(`/call/${matchId}/video`)}
                    className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-full py-4"
                  >
                    <Video className="w-5 h-5 mr-2" />
                    Video Call
                  </Button>
                  <Button
                    data-testid="voice-call-btn"
                    onClick={() => navigate(`/call/${matchId}/audio`)}
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white rounded-full py-4"
                  >
                    <Phone className="w-5 h-5 mr-2" />
                    Voice Call
                  </Button>
                </div>
              )}

              {/* Block Button */}
              <div className="mt-4 max-w-xs w-full">
                <BlockButton 
                  userId={profile.id}
                  userName={profile.name}
                  isBlocked={profile.i_blocked_them}
                  onBlockChange={() => loadProfile()}
                  size="sm"
                />
              </div>

            </div>
          </div>

          {/* Profile Details */}
          <div className="space-y-6">
            {/* Basic Info Card */}
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
              <div className="flex items-baseline space-x-3 mb-4">
                <h2 className="text-3xl font-bold text-gray-800">{profile.name}</h2>
                {profile.verification_status === 'approved' && (
                  <div className="flex items-center space-x-1 bg-blue-100 px-2 py-1 rounded-full">
                    <CheckCircle className="w-4 h-4 text-blue-600" />
                    <span className="text-xs font-semibold text-blue-700">Verified</span>
                  </div>
                )}
                {profile.is_blocked && (
                  <div className="flex items-center space-x-1 bg-red-100 px-2 py-1 rounded-full">
                    <Ban className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-semibold text-red-700">Blocked</span>
                  </div>
                )}
                <span className="text-2xl text-gray-600">{profile.age}</span>
              </div>
              
              {profile.location && (
                <div className="flex items-center space-x-2 text-gray-600 mb-4">
                  <MapPin className="w-5 h-5" />
                  <span>{profile.location}</span>
                </div>
              )}

              {profile.relationship_status && (
                <div className="flex items-center space-x-2 mb-4">
                  <div className={`px-3 py-1 rounded-full ${
                    profile.relationship_status === 'single' 
                      ? 'bg-green-100 text-green-700' 
                      : profile.relationship_status === 'in_relationship'
                      ? 'bg-pink-100 text-pink-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}>
                    <span className="text-sm font-semibold">
                      {profile.relationship_status === 'single' 
                        ? '💚 Single' 
                        : profile.relationship_status === 'in_relationship'
                        ? '💕 In a Relationship'
                        : '💍 Married'}
                    </span>
                  </div>
                </div>
              )}

              {profile.compatibility_score && (
                <div className="bg-purple-50 rounded-xl p-4 border border-purple-200">
                  <div className="flex items-center space-x-2 mb-2">
                    <Sparkles className="w-5 h-5 text-purple-500" />
                    <span className="font-bold text-purple-700 text-lg">
                      {profile.compatibility_score}% Match
                    </span>
                  </div>
                  {profile.compatibility_reason && (
                    <p className="text-sm text-gray-700">{profile.compatibility_reason}</p>
                  )}
                </div>
              )}
            </div>

            {/* About Card */}
            {profile.bio && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">About</h3>
                <p className="text-gray-700 leading-relaxed">{profile.bio}</p>
              </div>
            )}

            {/* Interests Card */}
            {profile.interests?.length > 0 && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Interests</h3>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, idx) => (
                    <Badge 
                      key={idx} 
                      className="px-4 py-2 bg-gradient-to-r from-pink-100 to-purple-100 text-purple-700 border border-purple-200 text-sm"
                    >
                      {interest}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Looking For Card */}
            {profile.looking_for && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-3">Looking For</h3>
                <p className="text-gray-700 capitalize">{profile.looking_for}</p>
              </div>
            )}

            {/* Date Place Ideas Card */}
            {profile.date_place_photos?.length > 0 && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-bold text-gray-800 mb-4">Date Place Ideas 💕</h3>
                <p className="text-sm text-gray-600 mb-4">Places {profile.name.split(' ')[0]} would love to go on a date</p>
                <div className="grid grid-cols-2 gap-3">
                  {profile.date_place_photos.map((photo, idx) => (
                    <div key={idx} className="relative group">
                      <img 
                        src={photo?.startsWith('http') ? photo : `${BACKEND_URL}${photo}`} 
                        alt={`Date place ${idx + 1}`} 
                        className="w-full h-40 object-cover rounded-xl shadow-md hover:shadow-xl cursor-pointer transform hover:scale-105"
                        style={{transition: 'all 0.3s ease'}}
                        onClick={() => window.open(photo?.startsWith('http') ? photo : `${BACKEND_URL}${photo}`, '_blank')}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Countries to Visit Card */}
            {profile.countries_to_visit?.length > 0 && (
              <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-lg">
                <div className="flex items-center space-x-2 mb-4">
                  <Globe className="w-6 h-6 text-blue-600" />
                  <h3 className="text-xl font-bold text-gray-800">Countries to Visit</h3>
                </div>
                <p className="text-sm text-gray-600 mb-4">Dream destinations {profile.name.split(' ')[0]} wants to explore</p>
                <div className="flex flex-wrap gap-2">
                  {profile.countries_to_visit.map((country, idx) => (
                    <Badge 
                      key={idx} 
                      className="px-4 py-2 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 border border-blue-200 text-sm font-medium"
                    >
                      {country}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Premium Badge */}
            {profile.is_premium && (
              <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-2xl p-4 border-2 border-yellow-300 text-center">
                <span className="text-yellow-700 font-bold">👑 Premium Member</span>
              </div>
            )}

            {/* Vote for Contest Card - Only show if user opted into contest */}
            {!isOwnProfile && profile?.contest_opt_in && (
              <div className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-6 shadow-lg border-2 border-pink-300">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center">
                      <Trophy className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-800">Vote in Dating Contest</h3>
                      <p className="text-sm text-gray-600">Help them win an all expense paid date or vacation!</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white/80 rounded-xl p-4 mb-4">
                  <div className="flex items-center justify-center space-x-2">
                    <Trophy className="w-5 h-5 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-700">{voteCount}</span>
                    <span className="text-gray-600">vote{voteCount !== 1 ? 's' : ''}</span>
                  </div>
                </div>

                {/* Daily Vote Status - Show timer after user has voted */}
                {dailyVoteStatus && hasVoted && (
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-3 mb-3 border border-blue-200">
                    <div className="flex items-center justify-center space-x-2 text-blue-700">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        Next free vote in {dailyVoteStatus.hours_until_reset}h {dailyVoteStatus.minutes_until_reset}m
                      </span>
                    </div>
                    <p className="text-xs text-center text-blue-600 mt-1">
                      Resets daily at 6:00 AM (Guyana Time)
                    </p>
                  </div>
                )}

                {/* Free Daily Vote Indicator */}
                {dailyVoteStatus?.can_use_free_vote && !hasVoted && (
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 mb-3 border border-green-200">
                    <p className="text-center text-sm font-medium text-green-700">
                      ✨ You have 1 FREE vote today!
                    </p>
                  </div>
                )}

                <Button
                  data-testid="vote-btn"
                  onClick={handleVote}
                  disabled={hasVoted || voting || (dailyVoteStatus && !dailyVoteStatus.can_use_free_vote)}
                  className={`w-full py-6 text-lg font-semibold rounded-full ${
                    hasVoted || (dailyVoteStatus && !dailyVoteStatus.can_use_free_vote)
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed' 
                      : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white'
                  }`}
                >
                  <Trophy className="w-5 h-5 mr-2" />
                  {hasVoted ? 'Already Voted' : voting ? 'Voting...' : dailyVoteStatus?.can_use_free_vote ? '🎁 Use Free Vote' : 'Vote Now'}
                </Button>

                {/* Gift Votes Button */}
                <Button
                  data-testid="gift-votes-btn"
                  onClick={() => setShowGiveVotesModal(true)}
                  className="w-full py-6 text-lg font-semibold rounded-full mt-3 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
                >
                  <Gift className="w-5 h-5 mr-2" />
                  Gift Votes ({voteBalance} available)
                </Button>

                {hasVoted && (
                  <div className="text-center mt-3">
                    <p className="text-sm text-purple-600 font-medium">
                      ✨ Thanks for voting!
                    </p>
                    {dailyVoteStatus && (
                      <p className="text-xs text-gray-500 mt-1">
                        🕐 Next free vote in {dailyVoteStatus.hours_until_reset}h {dailyVoteStatus.minutes_until_reset}m
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Give Votes Modal */}
      {showGiveVotesModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-sm w-full p-6 animate-in zoom-in-95">
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mb-4">
                <Gift className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-xl font-bold text-gray-800">Gift Votes to {profile?.name}</h2>
              <p className="text-sm text-gray-500 mt-1">Your balance: {voteBalance} votes</p>
            </div>

            {voteBalance === 0 ? (
              <div className="text-center">
                <p className="text-gray-600 mb-4">You don't have any votes to give.</p>
                <Button
                  onClick={() => {
                    setShowGiveVotesModal(false);
                    navigate('/buy-votes');
                  }}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  <Wallet className="w-4 h-4 mr-2" />
                  Buy Votes
                </Button>
                <Button
                  onClick={() => setShowGiveVotesModal(false)}
                  variant="ghost"
                  className="w-full mt-2"
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <>
                {/* Vote Amount Selector */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">How many votes?</label>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={() => setVotesToGive(Math.max(1, votesToGive - 1))}
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                    >
                      -
                    </Button>
                    <input
                      type="number"
                      value={votesToGive}
                      onChange={(e) => setVotesToGive(Math.min(voteBalance, Math.max(1, parseInt(e.target.value) || 1)))}
                      className="w-20 text-center text-2xl font-bold border-2 border-purple-200 rounded-xl py-2"
                      min="1"
                      max={voteBalance}
                    />
                    <Button
                      onClick={() => setVotesToGive(Math.min(voteBalance, votesToGive + 1))}
                      variant="outline"
                      size="icon"
                      className="rounded-full"
                    >
                      +
                    </Button>
                  </div>
                  
                  {/* Quick Select Buttons */}
                  <div className="flex flex-wrap gap-2 mt-3 justify-center">
                    {[1, 5, 10, 25, 50].filter(n => n <= voteBalance).map(num => (
                      <button
                        key={num}
                        onClick={() => setVotesToGive(num)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          votesToGive === num 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                    {voteBalance > 50 && (
                      <button
                        onClick={() => setVotesToGive(voteBalance)}
                        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${
                          votesToGive === voteBalance 
                            ? 'bg-purple-500 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        All ({voteBalance})
                      </button>
                    )}
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleGiveVotes}
                    disabled={givingVotes || votesToGive <= 0}
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white py-3"
                  >
                    {givingVotes ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Gift className="w-4 h-4 mr-2" />
                    )}
                    Give {votesToGive} Vote{votesToGive !== 1 ? 's' : ''}
                  </Button>
                  <Button
                    onClick={() => navigate('/buy-votes')}
                    variant="outline"
                    className="w-full"
                  >
                    <Wallet className="w-4 h-4 mr-2" />
                    Buy More Votes
                  </Button>
                  <Button
                    onClick={() => setShowGiveVotesModal(false)}
                    variant="ghost"
                    className="w-full"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
